/**
 * Order Notification Service
 * Handles email and SMS notifications for online orders
 */

import { API_ENDPOINTS } from '../config/apiRouting';

export interface OrderNotificationData {
  orderId: string;
  storeId: string;
  storeName: string;
  orderType: 'pickup' | 'dine-in' | 'delivery';
  customerInfo: {
    name: string;
    email?: string;
    phone?: string;
  };
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
  subtotal: number;
  tax: number;
  tip: number;
  total: number;
  estimatedReadyTime?: string;
  status: string;
}

export interface NotificationResult {
  success: boolean;
  emailSent?: boolean;
  smsSent?: boolean;
  error?: string;
}

// Notification templates
const EMAIL_TEMPLATES = {
  ORDER_CONFIRMED: {
    subject: 'Order Confirmed - #{orderId}',
    getBody: (data: OrderNotificationData) => `
      <h2>Thank you for your order, ${data.customerInfo.name}!</h2>
      <p>Your order has been confirmed at <strong>${data.storeName}</strong>.</p>
      <p><strong>Order #:</strong> ${data.orderId.slice(-8).toUpperCase()}</p>
      <p><strong>Order Type:</strong> ${data.orderType.charAt(0).toUpperCase() + data.orderType.slice(1)}</p>
      ${data.estimatedReadyTime ? `<p><strong>Estimated Ready:</strong> ${new Date(data.estimatedReadyTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</p>` : ''}
      <hr>
      <h3>Order Summary</h3>
      <ul>
        ${data.items.map(item => `<li>${item.quantity}x ${item.name} - $${(item.price * item.quantity).toFixed(2)}</li>`).join('')}
      </ul>
      <p><strong>Subtotal:</strong> $${data.subtotal.toFixed(2)}</p>
      <p><strong>Tax:</strong> $${data.tax.toFixed(2)}</p>
      ${data.tip > 0 ? `<p><strong>Tip:</strong> $${data.tip.toFixed(2)}</p>` : ''}
      <p><strong>Total:</strong> $${data.total.toFixed(2)}</p>
      <hr>
      <p>Thank you for choosing ${data.storeName}!</p>
    `
  },
  ORDER_READY: {
    subject: 'Your Order is Ready! - #{orderId}',
    getBody: (data: OrderNotificationData) => `
      <h2>Your order is ready, ${data.customerInfo.name}!</h2>
      <p><strong>Order #:</strong> ${data.orderId.slice(-8).toUpperCase()}</p>
      ${data.orderType === 'pickup'
        ? '<p>Please proceed to the pickup counter at <strong>' + data.storeName + '</strong> to collect your order.</p>'
        : data.orderType === 'delivery'
        ? '<p>Your order is on its way! Our driver will arrive shortly.</p>'
        : '<p>Your order is being brought to your table.</p>'
      }
      <p>Thank you for your order!</p>
    `
  },
  ORDER_OUT_FOR_DELIVERY: {
    subject: 'Your Order is On the Way! - #{orderId}',
    getBody: (data: OrderNotificationData) => `
      <h2>Your order is on its way, ${data.customerInfo.name}!</h2>
      <p><strong>Order #:</strong> ${data.orderId.slice(-8).toUpperCase()}</p>
      <p>Our driver has picked up your order from <strong>${data.storeName}</strong> and is heading your way.</p>
      <p>Thank you for choosing ${data.storeName}!</p>
    `
  }
};

const SMS_TEMPLATES = {
  ORDER_CONFIRMED: (data: OrderNotificationData) =>
    `${data.storeName}: Order #${data.orderId.slice(-8).toUpperCase()} confirmed! ${data.orderType === 'pickup' ? 'Est. ready: ' + (data.estimatedReadyTime ? new Date(data.estimatedReadyTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '15-20 min') : ''} Total: $${data.total.toFixed(2)}`,
  ORDER_READY: (data: OrderNotificationData) =>
    `${data.storeName}: Your order #${data.orderId.slice(-8).toUpperCase()} is READY! ${data.orderType === 'pickup' ? 'Please come to the pickup counter.' : data.orderType === 'delivery' ? 'Driver arriving soon!' : ''}`,
  ORDER_OUT_FOR_DELIVERY: (data: OrderNotificationData) =>
    `${data.storeName}: Your order #${data.orderId.slice(-8).toUpperCase()} is on the way! Driver has picked up your order.`
};

/**
 * Send order confirmation notifications (email and/or SMS)
 */
export async function sendOrderConfirmation(data: OrderNotificationData): Promise<NotificationResult> {
  const results = {
    success: false,
    emailSent: false,
    smsSent: false,
    error: undefined as string | undefined
  };

  try {
    const promises: Promise<any>[] = [];

    // Send email if email provided
    if (data.customerInfo.email) {
      promises.push(sendOrderEmail(data, 'ORDER_CONFIRMED'));
    }

    // Send SMS if phone provided
    if (data.customerInfo.phone) {
      promises.push(sendOrderSMS(data, 'ORDER_CONFIRMED'));
    }

    const outcomes = await Promise.allSettled(promises);

    // Process results
    let emailIndex = 0;
    let smsIndex = data.customerInfo.email ? 1 : 0;

    if (data.customerInfo.email && outcomes[emailIndex]) {
      const emailResult = outcomes[emailIndex];
      results.emailSent = emailResult.status === 'fulfilled' && emailResult.value?.success;
    }

    if (data.customerInfo.phone && outcomes[smsIndex]) {
      const smsResult = outcomes[smsIndex];
      results.smsSent = smsResult.status === 'fulfilled' && smsResult.value?.success;
    }

    results.success = results.emailSent || results.smsSent;
  } catch (error: any) {
    console.error('Error sending order notifications:', error);
    results.error = error.message;
  }

  return results;
}

/**
 * Send order ready notifications
 */
export async function sendOrderReadyNotification(data: OrderNotificationData): Promise<NotificationResult> {
  const results = {
    success: false,
    emailSent: false,
    smsSent: false,
    error: undefined as string | undefined
  };

  try {
    const promises: Promise<any>[] = [];

    if (data.customerInfo.email) {
      promises.push(sendOrderEmail(data, 'ORDER_READY'));
    }

    if (data.customerInfo.phone) {
      promises.push(sendOrderSMS(data, 'ORDER_READY'));
    }

    const outcomes = await Promise.allSettled(promises);

    let emailIndex = 0;
    let smsIndex = data.customerInfo.email ? 1 : 0;

    if (data.customerInfo.email && outcomes[emailIndex]) {
      const emailResult = outcomes[emailIndex];
      results.emailSent = emailResult.status === 'fulfilled' && emailResult.value?.success;
    }

    if (data.customerInfo.phone && outcomes[smsIndex]) {
      const smsResult = outcomes[smsIndex];
      results.smsSent = smsResult.status === 'fulfilled' && smsResult.value?.success;
    }

    results.success = results.emailSent || results.smsSent;
  } catch (error: any) {
    console.error('Error sending order ready notifications:', error);
    results.error = error.message;
  }

  return results;
}

/**
 * Send delivery status notifications
 */
export async function sendDeliveryNotification(data: OrderNotificationData, status: 'out_for_delivery' | 'delivered'): Promise<NotificationResult> {
  const results = {
    success: false,
    emailSent: false,
    smsSent: false,
    error: undefined as string | undefined
  };

  try {
    const templateKey = status === 'out_for_delivery' ? 'ORDER_OUT_FOR_DELIVERY' : 'ORDER_READY';
    const promises: Promise<any>[] = [];

    if (data.customerInfo.email) {
      promises.push(sendOrderEmail(data, templateKey as keyof typeof EMAIL_TEMPLATES));
    }

    if (data.customerInfo.phone) {
      promises.push(sendOrderSMS(data, templateKey as keyof typeof SMS_TEMPLATES));
    }

    const outcomes = await Promise.allSettled(promises);

    let emailIndex = 0;
    let smsIndex = data.customerInfo.email ? 1 : 0;

    if (data.customerInfo.email && outcomes[emailIndex]) {
      const emailResult = outcomes[emailIndex];
      results.emailSent = emailResult.status === 'fulfilled' && emailResult.value?.success;
    }

    if (data.customerInfo.phone && outcomes[smsIndex]) {
      const smsResult = outcomes[smsIndex];
      results.smsSent = smsResult.status === 'fulfilled' && smsResult.value?.success;
    }

    results.success = results.emailSent || results.smsSent;
  } catch (error: any) {
    console.error('Error sending delivery notifications:', error);
    results.error = error.message;
  }

  return results;
}

/**
 * Internal: Send email via API
 */
async function sendOrderEmail(data: OrderNotificationData, templateKey: keyof typeof EMAIL_TEMPLATES): Promise<{ success: boolean }> {
  try {
    const template = EMAIL_TEMPLATES[templateKey];
    const subject = template.subject.replace('#{orderId}', data.orderId.slice(-8).toUpperCase());
    const body = template.getBody(data);

    const response = await fetch(`${API_ENDPOINTS.messaging?.url || ''}/api/notifications/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: data.customerInfo.email,
        subject,
        html: body,
        storeId: data.storeId,
        orderId: data.orderId,
        type: templateKey.toLowerCase()
      })
    });

    if (response.ok) {
      const result = await response.json();
      return { success: result.success !== false };
    }

    return { success: false };
  } catch (error) {
    console.error('Failed to send order email:', error);
    return { success: false };
  }
}

/**
 * Internal: Send SMS via API
 */
async function sendOrderSMS(data: OrderNotificationData, templateKey: keyof typeof SMS_TEMPLATES): Promise<{ success: boolean }> {
  try {
    const template = SMS_TEMPLATES[templateKey];
    const message = template(data);

    const response = await fetch(`${API_ENDPOINTS.messaging?.url || ''}/api/notifications/sms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phoneNumber: data.customerInfo.phone,
        message,
        storeId: data.storeId,
        orderId: data.orderId,
        type: templateKey.toLowerCase()
      })
    });

    if (response.ok) {
      const result = await response.json();
      return { success: result.success !== false };
    }

    return { success: false };
  } catch (error) {
    console.error('Failed to send order SMS:', error);
    return { success: false };
  }
}

export default {
  sendOrderConfirmation,
  sendOrderReadyNotification,
  sendDeliveryNotification
};
